import {
  Button,
  Card,
  Checkbox,
  Input,
  Space,
  Spin,
  Alert,
  Calendar,
  Select,
  Row,
  Col,
  DatePicker,
  notification,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/lt";
import locale from "antd/es/date-picker/locale/lt_LT";
import type { Dayjs } from "dayjs";
import { useState, useEffect, useCallback } from "react";
import { useFormBase, City } from "../shared/useFormBase";
import StepIndicator from "../shared/StepIndicator";

import CardPartial from "../../partials/Card";
import type { FormConfig } from "../../App";

interface CampDateInfo {
  date: string;
  full_day_enabled: boolean;
  half_day_enabled: boolean;
}

interface CampFormProps {
  onSubmitSuccess: () => void;
  reservationType: string | null;
  formConfig: FormConfig;
}

const CampForm: React.FC<CampFormProps> = ({
  onSubmitSuccess,
  reservationType,
  formConfig,
}) => {
  const [selectedWeek, setSelectedWeek] = useState<string>("");
  const [campDates, setCampDates] = useState<CampDateInfo[]>([]);
  const [campDatesLoading, setCampDatesLoading] = useState(false);

  const form = useFormBase({
    reservationType,
    formConfig,
    onSubmitSuccess,
    formTemplate: "camp",
    campDates,
    selectedWeek,
  });

  const apiBaseUrl = process.env.REACT_APP_API_URL;

  const totalSteps = 3;
  const stepLabels = [
    "Savaitė ir kelialapis",
    "Vaiko informacija",
    "Santrauka",
  ];

  // Fetch camp available dates when city changes
  useEffect(() => {
    if (form.selectedCity && reservationType) {
      setCampDatesLoading(true);
      axios
        .get(`${apiBaseUrl}/api/calendar/exceptions/camp-dates`, {
          params: {
            city_uuid: form.selectedCity.uuid,
            reservation_type_uuid: reservationType,
          },
        })
        .then((res) => {
          const dates = Array.isArray(res.data) ? res.data : [];
          setCampDates(dates);
        })
        .catch(() => {
          setCampDates([]);
        })
        .finally(() => {
          setCampDatesLoading(false);
        });
    } else {
      setCampDates([]);
    }
  }, [form.selectedCity, reservationType, apiBaseUrl]);

  // Get pass availability for the selected week (Mon-Fri)
  const getWeekPassAvailability = useCallback(() => {
    if (!selectedWeek) return { full_day: false, half_day: false };

    const monday = dayjs(selectedWeek);
    const weekDates: string[] = [];
    for (let i = 0; i < 5; i++) {
      weekDates.push(monday.add(i, "day").format("YYYY-MM-DD"));
    }

    // Check all camp dates in this week
    const weekCampDates = campDates.filter((cd) => weekDates.includes(cd.date));
    if (weekCampDates.length === 0) return { full_day: false, half_day: false };

    // Pass is available if ANY date in the week has it enabled
    const full_day = weekCampDates.some((cd) => cd.full_day_enabled);
    const half_day = weekCampDates.some((cd) => cd.half_day_enabled);

    return { full_day, half_day };
  }, [selectedWeek, campDates]);

  // Auto-select pass type when only one is available
  useEffect(() => {
    const avail = getWeekPassAvailability();
    if (avail.full_day && !avail.half_day) {
      form.setCampPassType("full_day");
    } else if (!avail.full_day && avail.half_day) {
      form.setCampPassType("half_day");
    }
  }, [selectedWeek, campDates]);

  // Fetch available times when city and week are selected
  useEffect(() => {
    if (form.selectedCity && selectedWeek) {
      form.fetchTimes(form.selectedCity.uuid, selectedWeek);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.selectedCity, selectedWeek, form.fetchTimes]);

  // Format "HH:MM:SS" or "HH:MM" → "H:MM" (e.g. "08:30:00" → "8:30")
  const formatTime = (t: string): string => {
    if (!t) return "";
    const [h, m] = t.split(":");
    return `${parseInt(h, 10)}:${m || "00"}`;
  };

  const fullDayTime = form.availableTimes.find(
    (t: any) => t.camp_pass_type === "full_day",
  );
  const halfDayTime = form.availableTimes.find(
    (t: any) => t.camp_pass_type === "half_day",
  );
  const fullDayLabel = fullDayTime
    ? `${formatTime(fullDayTime.time_start)}-${formatTime(fullDayTime.time_end)}`
    : "";
  const halfDayLabel = halfDayTime
    ? `${formatTime(halfDayTime.time_start)}-${formatTime(halfDayTime.time_end)}`
    : "";

  // Get Monday of the week for a given date
  const getMondayOfWeek = (date: Dayjs): Dayjs => {
    const day = date.day(); // 0=Sunday, 1=Monday, ...
    if (day === 0) return date.subtract(6, "day");
    return date.subtract(day - 1, "day");
  };

  const handleCalendarSelect = (date: Dayjs) => {
    const monday = getMondayOfWeek(date);
    const mondayStr = monday.format("YYYY-MM-DD");
    setSelectedWeek(mondayStr);
    form.handleDateChange(mondayStr);
  };

  // Disable dates: past dates, weekends, and dates without camp exceptions
  const calendarFilter = (current: Dayjs) => {
    if (current < dayjs().startOf("day")) return true;
    const day = current.day();
    if (day === 0 || day === 6) return true; // Disable weekends

    // If we have camp dates loaded, only enable dates that appear in camp exceptions
    if (campDates.length > 0) {
      const dateStr = current.format("YYYY-MM-DD");
      const hasCampDate = campDates.some((cd) => cd.date === dateStr);
      return !hasCampDate; // Disable if NOT in camp dates
    }

    return false;
  };

  // Check if a date is in the selected week (Mon-Fri)
  const isInSelectedWeek = (date: Dayjs): boolean => {
    if (!selectedWeek) return false;
    const monday = dayjs(selectedWeek);
    const friday = monday.add(4, "day");
    return date >= monday.startOf("day") && date <= friday.endOf("day");
  };

  const canGoNext = () => {
    switch (form.currentStep) {
      case 0:
        return !!form.selectedCity && !!selectedWeek && !!form.campPassType;
      case 1:
        return (
          form.childName.length >= 2 &&
          form.childBirthDate.length > 0 &&
          form.parentName.length >= 3 &&
          form.phone.length === 8 &&
          form.customerEmail.length > 0 &&
          form.address.length >= 5
        );
      case 2:
        return form.confirmationChecked && form.dataProcessingConsent;
      default:
        return false;
    }
  };

  const handleSubmit = () => {
    form.submitOrder({
      print_invites: false,
      phone_in_invitation: false,
    });
  };

  const renderStep = () => {
    switch (form.currentStep) {
      case 0:
        return (
          <>
            <CardPartial isPosting={form.isPosting} active_time={"none"}>
              <div style={{ textAlign: "center" }}>
                <Space>
                  {form.cities.map((city: City) => (
                    <Button
                      type={
                        form.selectedCity?.uuid === city.uuid
                          ? "primary"
                          : "default"
                      }
                      key={city.uuid}
                      onClick={() => form.handleCityChange(city)}
                    >
                      {city.name}
                    </Button>
                  ))}
                </Space>
              </div>
            </CardPartial>

            {form.selectedCity ? (
              <>
                <CardPartial isPosting={form.isPosting} active_time={"none"}>
                  <div style={{ textAlign: "center" }}>
                    Pasirinktas miestas:{" "}
                    <span style={{ fontWeight: "bold" }}>
                      {form.selectedCity.name}
                    </span>
                  </div>
                </CardPartial>

                <CardPartial isPosting={form.isPosting} active_time={"none"}>
                  <Spin
                    tip="Kraunasi"
                    size="large"
                    spinning={form.loading || campDatesLoading}
                  >
                    <div className="camp-calendar-wrapper">
                      <Calendar
                        onSelect={handleCalendarSelect}
                        value={selectedWeek ? dayjs(selectedWeek) : undefined}
                        disabledDate={calendarFilter}
                        locale={locale}
                        fullCellRender={(date) => {
                          const isSelected = isInSelectedWeek(date);
                          const isDisabled = calendarFilter(date);
                          const isToday = date.isSame(dayjs(), "day");
                          const isCurrentMonth =
                            date.month() === (date as any).$d?.getMonth?.();

                          return (
                            <div
                              className="camp-calendar-cell"
                              style={{
                                ...(isSelected && !isDisabled
                                  ? {
                                      backgroundColor: "#722ed1",
                                      color: "#fff",
                                      borderRadius: 4,
                                    }
                                  : {}),
                                ...(isDisabled
                                  ? {
                                      color: "#ccc",
                                    }
                                  : {}),
                                ...(isToday && !isSelected
                                  ? {
                                      border: "1px solid #722ed1",
                                      borderRadius: 4,
                                    }
                                  : {}),
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {date.date()}
                            </div>
                          );
                        }}
                        headerRender={({ value, onChange }) => {
                          const months: string[] = [];
                          for (let i = 0; i < 12; i++) {
                            const m = value.clone().month(i);
                            const name = m.localeData().months(m).toString();
                            months.push(
                              name.charAt(0).toUpperCase() + name.slice(1),
                            );
                          }

                          return (
                            <div style={{ padding: 8 }}>
                              <Row
                                gutter={8}
                                justify="space-between"
                                align="middle"
                              >
                                <Col>
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      onChange(
                                        value.clone().subtract(1, "month"),
                                      )
                                    }
                                    icon={<LeftOutlined />}
                                  />
                                </Col>
                                <Col>
                                  <Space size={4}>
                                    <Select
                                      size="small"
                                      value={value.year()}
                                      style={{ minWidth: "80px" }}
                                      onChange={(y) =>
                                        onChange(value.clone().year(y))
                                      }
                                    >
                                      {Array.from(
                                        { length: 20 },
                                        (_, i) => value.year() - 10 + i,
                                      ).map((y) => (
                                        <Select.Option key={y} value={y}>
                                          {y}
                                        </Select.Option>
                                      ))}
                                    </Select>
                                    <Select
                                      size="small"
                                      value={value.month()}
                                      style={{ minWidth: "100px" }}
                                      onChange={(m) =>
                                        onChange(value.clone().month(m))
                                      }
                                    >
                                      {months.map((m, i) => (
                                        <Select.Option key={i} value={i}>
                                          {m}
                                        </Select.Option>
                                      ))}
                                    </Select>
                                  </Space>
                                </Col>
                                <Col>
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      onChange(value.clone().add(1, "month"))
                                    }
                                    icon={<RightOutlined />}
                                  />
                                </Col>
                              </Row>
                            </div>
                          );
                        }}
                        validRange={[dayjs(), dayjs().add(30, "month")]}
                        fullscreen={false}
                      />
                    </div>
                  </Spin>
                </CardPartial>

                <CardPartial
                  isPosting={form.isPosting}
                  title="Kelialapiai"
                  active_time={"none"}
                >
                  <Space
                    style={{ width: "100%", justifyContent: "center" }}
                    wrap
                    direction="horizontal"
                  >
                    {getWeekPassAvailability().full_day && fullDayLabel && (
                      <Button
                        type={
                          form.campPassType === "full_day"
                            ? "primary"
                            : "default"
                        }
                        onClick={() => form.setCampPassType("full_day")}
                        size="small"
                      >
                        {fullDayLabel}
                      </Button>
                    )}
                    {getWeekPassAvailability().half_day && halfDayLabel && (
                      <Button
                        type={
                          form.campPassType === "half_day"
                            ? "primary"
                            : "default"
                        }
                        onClick={() => form.setCampPassType("half_day")}
                        size="small"
                      >
                        {halfDayLabel}
                      </Button>
                    )}
                  </Space>
                  {!selectedWeek && (
                    <Alert
                      type="info"
                      message="Pasirinkite savaitę kalendoriuje"
                      banner
                      style={{ marginTop: 8 }}
                    />
                  )}
                  {selectedWeek &&
                    !getWeekPassAvailability().full_day &&
                    !getWeekPassAvailability().half_day && (
                      <Alert
                        type="warning"
                        message="Šiai savaitei nėra galimų kelialapių"
                        banner
                        style={{ marginTop: 8 }}
                      />
                    )}
                </CardPartial>
              </>
            ) : (
              <Alert
                type="warning"
                message="Pirma pasirinkite miestą, kad matytumėte kalendorių"
                banner
              />
            )}
          </>
        );

      case 1:
        return (
          <CardPartial
            isPosting={form.isPosting}
            title="Kontaktinė informacija"
            active_time={form.activeTime || "none"}
          >
            <Space className="w-full" direction="vertical" wrap>
              <label>Vaiko vardas</label>
              <Input
                value={form.childName}
                onChange={(e) => form.setChildName(e.target.value)}
                placeholder="Vaiko vardas"
              />

              <label>Vaiko gimimo data</label>
              <DatePicker
                value={form.childBirthDate ? dayjs(form.childBirthDate) : null}
                onChange={(date) =>
                  form.setChildBirthDate(date ? date.format("YYYY-MM-DD") : "")
                }
                placeholder="Pasirinkite gimimo datą pvz. 2015-09-01"
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
                allowClear={false}
              />

              <label>Vieno iš tėvų vardas ir pavardė</label>
              <Input
                value={form.parentName}
                onChange={(e) => form.setParentName(e.target.value)}
                placeholder="Vieno iš tėvų vardas ir pavardė"
                autoComplete="name"
              />

              <label>Gyvenamosios vietos adresas</label>
              <Input
                value={form.address}
                onChange={(e) => form.setAddress(e.target.value)}
                placeholder="Gyvenamosios vietos adresas"
                autoComplete="street-address"
              />

              <label>Telefono numeris</label>
              <Input
                addonBefore="+370"
                value={form.phone}
                onChange={(e) => {
                  let value = e.target.value;
                  value = value.replace(/\D/g, "");
                  if (value.startsWith("00370")) {
                    value = value.substring(5);
                  } else if (value.startsWith("370")) {
                    value = value.substring(3);
                  }
                  if (
                    (value.startsWith("0") || value.startsWith("8")) &&
                    value.length > 8
                  ) {
                    value = value.substring(1);
                  }
                  value = value.substring(0, 8);
                  form.setPhone(value);
                }}
                placeholder="6000000"
                autoComplete="tel-national"
              />

              <label>El. paštas</label>
              <Input
                type="email"
                value={form.customerEmail}
                onChange={(e) => form.setCustomerEmail(e.target.value)}
                placeholder="El. paštas"
                autoComplete="email"
              />

              <label>Jei turite klausimų</label>
              <Input.TextArea
                value={form.comments}
                onChange={(e) => form.setComments(e.target.value)}
                placeholder="Rašykite čia..."
                rows={3}
              />
            </Space>
          </CardPartial>
        );

      case 2:
        return (
          <>
            <CardPartial
              isPosting={form.isPosting}
              title="Užsakymo santrauka"
              active_time={form.activeTime || "none"}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <div>
                  <strong>Miestas:</strong> {form.selectedCity?.name}
                </div>
                <div>
                  <strong>Savaitė:</strong> {selectedWeek} —{" "}
                  {dayjs(selectedWeek).add(4, "day").format("YYYY-MM-DD")}
                </div>
                <div>
                  <strong>Kelialapis:</strong>{" "}
                  {form.campPassType === "full_day"
                    ? `Visa diena${fullDayLabel ? ` (${fullDayLabel})` : ""}`
                    : `Pusė dienos${halfDayLabel ? ` (${halfDayLabel})` : ""}`}
                </div>
                <div>
                  <strong>Vaikas:</strong> {form.childName}
                </div>
                {form.childBirthDate && (
                  <div>
                    <strong>Gimimo data:</strong> {form.childBirthDate}
                  </div>
                )}
                <div>
                  <strong>Tėvai:</strong> {form.parentName}
                </div>
                <div>
                  <strong>Telefonas:</strong> +370{form.phone}
                </div>
                <div>
                  <strong>El. paštas:</strong> {form.customerEmail}
                </div>
                <div>
                  <strong>Adresas:</strong> {form.address}
                </div>
                {form.comments && (
                  <div>
                    <strong>Komentarai:</strong> {form.comments}
                  </div>
                )}
              </div>
            </CardPartial>

            <CardPartial
              isPosting={form.isPosting}
              title="Nuolaidos kodas"
              active_time={form.activeTime || "none"}
            >
              <Input
                value={form.discountCode}
                onChange={(e) => form.setDiscountCode(e.target.value)}
                placeholder="Nuolaidos kodas"
                style={{ width: "100%" }}
              />
              <div style={{ textAlign: "center", width: "100%" }}>
                <div style={{ marginTop: 8 }}>
                  {form.discountStatus === true && (
                    <span style={{ color: "green" }}>
                      <CheckCircleFilled /> Nuolaidos kodas pritaikytas
                    </span>
                  )}
                  {form.discountStatus === false && (
                    <span style={{ color: "red" }}>
                      <CloseCircleFilled /> Nuolaidos kodas nerastas
                    </span>
                  )}
                </div>
                <Button
                  onClick={form.checkDiscount}
                  type="primary"
                  style={{ marginTop: 8 }}
                  disabled={!form.selectedDate}
                >
                  Patikrinti
                </Button>
              </div>
            </CardPartial>

            <CardPartial
              isPosting={form.isPosting}
              title="Mokėtina suma"
              active_time={form.activeTime || "none"}
            >
              <div
                style={{
                  width: "100%",
                  fontSize: 48,
                  textAlign: "center",
                  color: "#D33092",
                }}
              >
                {form.price}
                <sup>
                  <small>EUR</small>
                </sup>
              </div>

              <div style={{ textAlign: "center", width: "100%" }}>
                <div
                  style={{
                    marginBottom: 16,
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <Checkbox
                    checked={form.marketingConsent}
                    onChange={(e) => form.setMarketingConsent(e.target.checked)}
                  >
                    <span>
                      {formConfig.gdpr_consent_text ||
                        "Sutinku gauti rinkodaros pranešimus ir pasiūlymus"}
                    </span>
                  </Checkbox>
                  <Checkbox
                    checked={form.confirmationChecked}
                    onChange={(e) => {
                      form.setConfirmationChecked(e.target.checked);
                      form.setDataProcessingConsent(e.target.checked);
                    }}
                  >
                    Patvirtinu, kad visi duomenys yra teisingi, sutinku su{" "}
                    <a
                      href="https://pramoguloftas.lt/privatumo-politika/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#66469C", textDecoration: "underline" }}
                    >
                      privatumo politika
                    </a>{" "}
                    bei paslaugų teikimo sąlygomis *
                  </Checkbox>
                </div>
              </div>
            </CardPartial>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: "400px", fontFamily: "Poppins, sans-serif" }}>
      <StepIndicator
        currentStep={form.currentStep}
        totalSteps={totalSteps}
        labels={stepLabels}
        onStepClick={(step) => {
          if (step <= form.currentStep) form.setCurrentStep(step);
        }}
      />

      {renderStep()}

      {form.currentStep < totalSteps - 1 ? (
        <Card style={{ marginTop: "16px", textAlign: "center" }}>
          {form.currentStep > 0 && (
            <Button onClick={form.prevStep} style={{ marginRight: "8px" }}>
              Atgal
            </Button>
          )}
          <Button
            type="primary"
            onClick={form.nextStep}
            disabled={!canGoNext()}
          >
            Kitas žingsnis
          </Button>
        </Card>
      ) : (
        <Card style={{ marginTop: "16px", textAlign: "center" }}>
          <Button onClick={form.prevStep} style={{ marginRight: "8px" }}>
            Atgal
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={form.isPosting}
            disabled={!canGoNext()}
          >
            Pateikti užsakymą
          </Button>
        </Card>
      )}
    </div>
  );
};

export default CampForm;
